/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { IComponent, IComponentProps, getCatalogId, msdyn365Commerce } from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    // addOrderTemplateToCartAction,
    // AddOrderTemplateToCartInput,
    ArrayExtensions,
    GetOrderTemplatesInput,
    OrderTemplate,
    OrderTemplateLine,
    getOrderTemplates,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    IPopupProductsProps,
    ITelemetryContent,
    PopupProducts,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { ProductsDataActions } from '@msdyn365-commerce/retail-proxy';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getProductListLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import React, { useState } from 'react';

import OrderListSupercededError from './order-list-superseded-issue.component';
import { ILinesAddedToCartDialogResources, LinesAddedToCartDialog } from './lines-added-to-cart-dialog.component';

export interface IAddOrderTemplateToCartComponentData {
    orderTemplate: OrderTemplate;
}

type ExtensionProperty = {
    Key?: string;
    Value?: {
        StringValue?: string;
        BooleanValue?: boolean;
    };
};

type UOMAvailableStore = {
    PickupStore: string;
};

const _getExtensionPropertyBooleanValue = (extensionProperties: ExtensionProperty[] | undefined, key: string): boolean =>
    extensionProperties?.find(prop => prop.Key === key)?.Value?.BooleanValue === true ||
    extensionProperties?.find(prop => prop.Key === key)?.Value?.StringValue === 'true';

const _isSupersededLine = (line: OrderTemplateLine): boolean => {
    if (_getExtensionPropertyBooleanValue(line.productListLine.ExtensionProperties, 'IsSupersededItem')) {
        return true;
    }

    const productExtensionProperties = (line.fullProduct?.ProductDetails as { ExtensionProperties?: ExtensionProperty[] } | undefined)
        ?.ExtensionProperties;
    return _getExtensionPropertyBooleanValue(productExtensionProperties, 'ItemSuperseded');
};

const _hasSupersededItemsInOrderTemplate = (orderTemplate: OrderTemplate | undefined): boolean => {
    const lines = orderTemplate?.orderTemplateLines ?? [];
    return lines.some(line => _isSupersededLine(line));
};

const _hasSupersededItemsFromApi = async (
    orderTemplateId: string,
    actionContext: IAddOrderTemplateToCartComponentProps['context']['actionContext']
): Promise<boolean> => {
    const channelId = actionContext.requestContext.apiSettings.channelId;
    if (!channelId) {
        console.log('[AddOrderTemplateToCart] Missing channelId for orderTemplateId:', orderTemplateId);
        return false;
    }

    const productLists = await getOrderTemplates(new GetOrderTemplatesInput(orderTemplateId), actionContext);
    const productList = productLists?.[0];
    console.log('[AddOrderTemplateToCart] productLists for orderTemplateId:', orderTemplateId, productLists);
    if (!productList?.Id) {
        console.log('[AddOrderTemplateToCart] No productList found for orderTemplateId:', orderTemplateId);
        return false;
    }

    const productListLines =
        (await getProductListLinesAsync(
            {
                callerContext: actionContext,
                queryResultSettings: { Paging: { Top: 500, Skip: 0 } },
                bypassCache: 'get'
            },
            productList.Id,
            ''
        )) ?? [];
    console.log('[AddOrderTemplateToCart] productListLines for productListId:', productList.Id, productListLines);

    const productIds = ArrayExtensions.unique(productListLines.map(line => line.ProductId).filter((id): id is number => !!id));
    console.log('[AddOrderTemplateToCart] productIds for productListId:', productList.Id, productIds);
    if (productIds.length === 0) {
        console.log('[AddOrderTemplateToCart] No productIds found for productListId:', productList.Id);
        return false;
    }

    const products = (await ProductsDataActions.getByIdsAsync({ callerContext: actionContext }, channelId, productIds)) ?? [];
    console.log('[AddOrderTemplateToCart] hydrated products for productListId:', productList.Id, products);
    const hasSupersededItems = products.some(product =>
        _getExtensionPropertyBooleanValue(product.ExtensionProperties as ExtensionProperty[] | undefined, 'ItemSuperseded')
    );
    console.log('[AddOrderTemplateToCart] hasSupersededItemsFromApi for productListId:', productList.Id, hasSupersededItems);
    return hasSupersededItems;
};

export interface IAddOrderTemplateToCartComponentProps extends IComponentProps<IAddOrderTemplateToCartComponentData> {
    className?: string;
    parentElementRef?: React.RefObject<Button>;
    addToCartText: string;
    telemetryContent?: ITelemetryContent;
    hasSupersededItems?: boolean;
    oosByProductId?: Record<number, boolean>;
    dialogStrings: {
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        orderTemplateErrorMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
    };
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogId?: number;
}

export declare type IAddToCartActionFailureReason = 'EMPTYINPUT' | 'MISSINGDIMENSION' | 'OUTOFSTOCK' | 'CARTACTIONFAILED'; // Rename to used method

export interface IAddOrderTemplateToCartComponent extends IComponent<IAddOrderTemplateToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddOrderTemplateToCartComponentProps) => void;
}

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setIsSupersededIssueOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    // console.log(props.data);
    openModal(true);
    setUploadStatus('LOADING');
    const { context, dialogStrings } = props;
    // console.log('[AddOrderTemplateToCart] props.hasSupersededItems:', props.hasSupersededItems);
    // console.log('[AddOrderTemplateToCart] orderTemplate from props:', props.data.orderTemplate);
    // console.log(
    //     '[AddOrderTemplateToCart] hasSupersededItemsInOrderTemplate:',
    //     _hasSupersededItemsInOrderTemplate(props.data.orderTemplate)
    // );
    const resolvedHasSupersededItems =
        props.hasSupersededItems === true ||
        _hasSupersededItemsInOrderTemplate(props.data.orderTemplate) ||
        (await _hasSupersededItemsFromApi(props.data.orderTemplate.productList.Id, context.actionContext));
    // console.log('[AddOrderTemplateToCart] resolvedHasSupersededItems:', resolvedHasSupersededItems);
    // assuming selected catalogId will be passed to component and if not passed then get from context
    const catalogId = props.catalogId ?? getCatalogId(props.context.actionContext.requestContext);
    validateCatalogId(catalogId);
    // const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;
    const cartState = await getCartState(props.context.actionContext);
    const cartId = cartState?.cart?.Id;
    // const addOrderTemplateToCartInput = isMultipleCatalogFeatureEnabledInHq
    //     ? new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id, isMultipleCatalogFeatureEnabledInHq)
    //     : new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id);
    try {
        const cartState = await getCartState(context.actionContext);
        const cart = cartState.cart;
        const hasInvoiceInCart = cart?.CartLines?.find(cartLine => cartLine.IsInvoiceLine === true);
        if (hasInvoiceInCart) {
            setErrorMessage('Can not add items to a cart containing an invoice.');
            setUploadStatus('FAILED');
        } else {
            const preferredStore = JSON.parse(localStorage.getItem('_msdyn365__preferredStore_') || '{}');

            const isStoreAvailableInUOM = (extensionProperties: ExtensionProperty[], selectedStore: string): boolean => {
                const uomStoreProp = extensionProperties.find(prop => prop.Key === 'UOMAvailableStores');

                if (!uomStoreProp?.Value?.StringValue || !selectedStore) {
                    return false;
                }

                try {
                    const stores: UOMAvailableStore[] = JSON.parse(uomStoreProp.Value.StringValue);

                    return stores.some(store => store.PickupStore === selectedStore);
                } catch (error) {
                    console.error('Failed to parse UOMAvailableStores JSON', error);
                    return false;
                }
            };

            const addProductToCart = [];
            let skippedSupersededItems = false;
            const createCartItem = (product: OrderTemplateLine) => ({
                DeliveryMode: 'BOPIS',
                FulfillmentStoreId: preferredStore?.StoreId,
                ItemId: product.fullProduct?.ProductDetails.ItemId,
                ProductId: product.productListLine.ProductId,
                Quantity: product.productListLine.Quantity,
                TrackingId: '',
                UnitOfMeasureSymbol: product.fullProduct?.ProductDetails.DefaultUnitOfMeasure,
                WarehouseId: preferredStore?.StoreId
            });
            if (props.data.orderTemplate.orderTemplateLines) {
                for (let i = 0; i < props.data.orderTemplate.orderTemplateLines.length; i++) {
                    const line = props.data.orderTemplate.orderTemplateLines[i];
                    // console.log('[AddOrderTemplateToCart] checking line:', line);
                    if (_isSupersededLine(line)) {
                        skippedSupersededItems = true;
                        // console.log('[AddOrderTemplateToCart] superseded line found:', line);
                        continue;
                    }
                    const storeBasedOOS = isStoreAvailableInUOM(
                        line.productListLine.ExtensionProperties ?? [],
                        preferredStore?.StoreId ?? ''
                    );
                    const productId = line.fullProduct?.ProductDetails.RecordId ?? line.productListLine.ProductId;
                    const availabilityOOS = productId ? props.oosByProductId?.[productId] === true : false;
                    console.log('[AddOrderTemplateToCart] availability check:', {
                        itemId: line.fullProduct?.ProductDetails.ItemId,
                        productId,
                        storeBasedOOS,
                        availabilityOOS
                    });

                    if (!storeBasedOOS && !availabilityOOS) {
                        addProductToCart.push(createCartItem(line));
                    } else {
                        throw new Error('Store-based or inventory OOS detected');
                    }
                }
            }

            if (addProductToCart.length === 0 && (skippedSupersededItems || resolvedHasSupersededItems)) {
                openModal(false);
                setUploadStatus('WAITING');
                setErrorMessage('');
                setIsSupersededIssueOpen(true);
                return;
            }

            const newCart = await addCartLinesAsync({ callerContext: props.context.actionContext }, cartId, addProductToCart);
            await cartState?.updateCart({ newCartObject: newCart });
            // const addedCartLines = await addOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);

            setAddedLinesCount(addProductToCart.length);
            openModal(false);
            setTimeout(() => {
                if (skippedSupersededItems || resolvedHasSupersededItems) {
                    setIsSupersededIssueOpen(true);
                } else {
                    setLinesAddedToCartDialogOpen(true);
                }
            }, 500);
            setErrorMessage('');
        }
    } catch (error) {
        setTimeout(() => {
            setErrorMessage(dialogStrings.orderTemplateErrorMessage);
            setUploadStatus('FAILED');
        }, 1000);
    }
};

const AddToCartComponentActions = {
    onClick
};

export type addOrderTemplateToCartStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'WAITING';

const _getModalText = (status: addOrderTemplateToCartStatus, props: IAddOrderTemplateToCartComponentProps, errorMsg: string) => {
    const { dialogStrings } = props;
    const { addToCartFailureMessage, addToCartSuccessMessage, addToCartProcessMessage } = dialogStrings;

    if (status === 'LOADING') {
        return `${addToCartProcessMessage}...`;
    }

    if (status === 'SUCCESS') {
        return addToCartSuccessMessage;
    }

    if (status === 'FAILED') {
        return errorMsg !== null ? errorMsg : addToCartFailureMessage;
    }

    return '';
};

let buttonEventReference: HTMLButtonElement | undefined;

const AddOrderTemplate: React.FC<IAddOrderTemplateToCartComponentProps> = (props: IAddOrderTemplateToCartComponentProps) => {
    const [uploadStatus, setUploadStatus] = useState<addOrderTemplateToCartStatus>('WAITING');
    const [modalOpen, setModalOpen] = useState(false);
    const [addedLinesCount, setAddedLinesCount] = useState(0);
    const [linesAddedToCartDialogOpen, setLinesAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSupersededIssueOpen, setIsSupersededIssueOpen] = useState(false);

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        buttonEventReference = event.target as HTMLButtonElement;
        await AddToCartComponentActions.onClick(
            event,
            props,
            setUploadStatus,
            setModalOpen,
            setLinesAddedToCartDialogOpen,
            setIsSupersededIssueOpen,
            setAddedLinesCount,
            setErrorMessage
        );
    };
    const closeLinesAddedToCartDialog = () => {
        setLinesAddedToCartDialogOpen(false);
        setErrorMessage('');
        buttonEventReference?.focus();
    };

    const modalText = _getModalText(uploadStatus, props, errorMessage);
    const processStatusClass = uploadStatus.toLowerCase();
    const dialogStrings = { closeWindowButtonText: props.dialogStrings.closeWindowButtonText };
    const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.AddOrderTemplateToCart);
    const addOrderTemplateAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

    const popupProps: IPopupProductsProps = {
        dialogStrings,
        className: 'msc-add-order-template-to-cart',
        processStatusClass,
        text: modalText,
        modalOpen,
        setModalOpen
    };

    const renderModalPopup = <PopupProducts {...popupProps} />;

    const products = props.data.orderTemplate.orderTemplateLines
        .map(line => line.fullProduct)
        .filter(fullProduct => fullProduct) as FullProduct[];
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;

    return (
        <>
            <OrderListSupercededError isError={isSupersededIssueOpen} setIsError={setIsSupersededIssueOpen} />
            {renderModalPopup}
            <button
                className={classnames(
                    'msc-add-order-template-to-cart msc-btn',
                    props.className,
                    props.data.orderTemplate.totalLines === 0 ? 'disabled' : ''
                )}
                aria-label={props.addToCartText}
                disabled={props.data.orderTemplate.totalLines === 0}
                type='button'
                onClick={onClickHandler}
                {...addOrderTemplateAttribute}
            >
                {props.addToCartText}
            </button>
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.dialogStrings.linesAddedToCartDialogResources}
                data={{
                    linesCount: addedLinesCount,
                    orderTemplate: props.data.orderTemplate.productList,
                    products
                }}
                modalOpen={linesAddedToCartDialogOpen}
                onClose={closeLinesAddedToCartDialog}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq || false}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
        </>
    );
};

export const AddOrderTemplateToCartComponent: React.FunctionComponent<IAddOrderTemplateToCartComponentProps> =
    // @ts-expect-error -- As per design, custom component
    msdyn365Commerce.createComponent<IAddOrderTemplateToCartComponent>('AddOrderTemplate', {
        component: AddOrderTemplate,
        ...AddToCartComponentActions
    });
